import { useEffect, useState } from 'react';
import {
  cleanAmount,
  getGroupBudgetsPayload,
} from 'src/components/oneCorp/features/MealCredit/hooks/assignAllowanceAmount.utils';
import { assignAllowanceAmount } from 'src/components/oneCorp/features/MealCredit/hooks/assignAllowanceAmountApi';
import { useAsyncTaskHandler } from 'src/common/utils/useAsyncTaskHandler';
import { LocTagInvite } from 'src/types/LineOfCredit';
import { transformPenniesToDollar } from 'src/utilities/currency-utils';

export interface UseAssignAllowanceAmount {
  tagInitializationStatus: TagInitialization;
  updateAllowanceAmount: (amount: string) => Promise<void>;
  amount: string;
}

export interface UseAssignAllowanceAmountProps {
  corpId: string;
  mealCreditId: string;
  tagInvites: LocTagInvite[];
  onSuccess?: () => Promise<void>;
  onError?: () => Promise<void>;
}
export enum TagInitialization {
  PROCESSING,
  NOT_INITIALIZED,
  INITIALIZED,
}

export const useAssignAllowanceAmount = ({
  corpId,
  mealCreditId,
  tagInvites,
  onError,
  onSuccess,
}: UseAssignAllowanceAmountProps): UseAssignAllowanceAmount => {
  const { startAsyncTask } = useAsyncTaskHandler({ onSuccess, onError });
  const [tagInitializationStatus, setTagInitializationStatus] =
    useState<TagInitialization>(TagInitialization.PROCESSING);
  const [tagInvite, setTagInvite] = useState<LocTagInvite>(null);
  const [amount, setAmount] = useState<string>('');

  useEffect(() => {
    const tagInvite = tagInvites.find(({ id }) => id === mealCreditId);
    const tagInviteToSet = tagInvite || {
      id: mealCreditId,
      budget: 0,
      name: mealCreditId,
    };

    setTagInvite(tagInviteToSet);
    const transformedAmount = transformPenniesToDollar(tagInviteToSet.budget);
    setAmount(transformedAmount);
    setTagInitializationStatus(
      tagInvite
        ? TagInitialization.INITIALIZED
        : TagInitialization.NOT_INITIALIZED
    );
  }, [tagInvites, mealCreditId]);

  async function updateAllowanceAmount(amount: string): Promise<void> {
    const updatedGroupBudgets = getUpdatedGroupBudgets(amount);

    await startAsyncTask(() => {
      return assignAllowanceAmount({
        corpId,
        mealCreditId,
        updatedGroupBudgets,
      });
    });
  }

  function getUpdatedGroupBudgets(amount: string): Record<string, number> {
    const budget = cleanAmount(amount);
    return getGroupBudgetsPayload([...tagInvites, { ...tagInvite, budget }]);
  }

  return {
    tagInitializationStatus,
    updateAllowanceAmount,
    amount,
  };
};
